import { DataManager } from "../model/DataManager.js";
import { VOCategory, VOVideo } from "../model/DataStructure.js";
import { Language } from "../model/Language.js";

export interface IVideoSelector {
    onSelectVideo: e5.Signal<[VOVideo]>;
    element: HTMLDivElement;
    category: VOCategory;
    video: VOVideo;
}

export class VideoSelector implements IVideoSelector {

    public onSelectVideo = e5.Signal.create<[VOVideo]>();

    public element: HTMLDivElement;

    private _track: HTMLDivElement;
    private _trackList: HTMLDivElement;
    private _items: VideoSelectorItem[] = [];
    private _videosVisible: VideoSelectorItem[] = [];
    private _category: VOCategory = null;
    private _video: VOVideo = null;
    private _prev: HTMLDivElement;
    private _next: HTMLDivElement;
    private _prevIcon: HTMLDivElement;
    private _nextIcon: HTMLDivElement;
    private _offset: number = 2;
    private _targetScrollLeft: number = null;

    constructor(private _videoData: VOVideo[], private _data: DataManager) {
        this.element = document.createElement("div");
        this.element.classList.add("video-selector");
        this.element.innerHTML = `
        <div class="video-selector-control">
            <div class="video-selector-track scrollbar horz">
                <div class="video-selector-track-list"></div>
            </div>
            <div class="video-selector-nav prev">
                <div class="video-selector-nav-icon prev e5 icon-125"></div>
            </div>
            <div class="video-selector-nav next">
                <div class="video-selector-nav-icon next e5 icon-124"></div>
            </div>
        </div>
        `;


        this._track = this.element.querySelector(".video-selector-track");
        this._trackList = this.element.querySelector(".video-selector-track-list");
        this._prev = this.element.querySelector(".video-selector-nav.prev");
        this._next = this.element.querySelector(".video-selector-nav.next");
        this._prevIcon = this.element.querySelector(".video-selector-nav-icon.prev");
        this._nextIcon = this.element.querySelector(".video-selector-nav-icon.next");

        for (var video of this._videoData) {
            var videoItem = new VideoSelectorItem(video);
            if (this._items.length == 0) {
                videoItem.element.classList.add("first");
            }
            this._items.push(videoItem);
            this._videosVisible.push(videoItem);
            this._trackList.appendChild(videoItem.element);
        }

        $(this.element).on("click", "[data-videoid]", (e) => {
            var videoId = parseInt(e.currentTarget.getAttribute("data-videoid"));
            var video = this._videoData.id(videoId);
            if (video) {
                this.onSelectVideo.dispatch(video);
            }
        });

        var resize = e5.Resize.create(this._trackList);
        resize.add(() => {
            this.updateNavIcons();
        });
        this._track.addEventListener("scroll", () => this.updateNavIcons());


        this._prevIcon.addEventListener("click", () => {
            var prev = this.getPrev();
            if (!prev) {
                return;
            }
            this.scrollTo(prev.element.offsetLeft);
        });

        this._nextIcon.addEventListener("click", () => {
            var next = this.getNext();
            if (!next) {
                return;
            }
            this.scrollTo(next.element.offsetLeft + next.element.offsetWidth - this._track.offsetWidth);
        });

        Language.onChange.add((language) => {
            this.updateVisibility();
        });
    }

    private getPrev(): VideoSelectorItem {
        var first = this.getFirstCompletlelyVisible();
        if (!first) {
            return this._videosVisible[0];
        }
        var index = this._videosVisible.indexOf(first);
        return this._videosVisible[e5.Calc.clamp(index - this._offset, 0, this._videosVisible.length - 1)];
    }

    private getNext(): VideoSelectorItem {
        var last = this.getLastCompletlelyVisible();
        if (!last) {
            return this._videosVisible[this._videosVisible.length - 1];
        }
        var index = this._videosVisible.indexOf(last);
        return this._videosVisible[e5.Calc.clamp(index + this._offset, 0, this._videosVisible.length - 1)];
    }

    private getLastCompletlelyVisible(): VideoSelectorItem {
        var right = this.targetScrollLeft + this._track.offsetWidth;
        for (var i = this._videosVisible.length - 1; i >= 0; i--) {
            var video = this._videosVisible[i];
            if (video.element.offsetLeft + video.element.offsetWidth <= right) {
                return video;
            }
        }
        return null;
    }

    private getFirstCompletlelyVisible(): VideoSelectorItem {
        var left = this.targetScrollLeft;
        for (var video of this._videosVisible) {
            if (video.element.offsetLeft >= left) {
                return video;
            }
        }
        return null;
    }

    private scrollTo(scrollLeft: number): void {
        scrollLeft = Math.round(scrollLeft);
        var maxScroll = e5.Calc.clampMin(this._track.scrollWidth - this._track.offsetWidth, 0);
        scrollLeft = e5.Calc.clamp(scrollLeft, 0, maxScroll);
        this._targetScrollLeft = scrollLeft;
        gsap.to(this._track, {
            duration: 0.5,
            scrollLeft: scrollLeft,
            onComplete: () => {
                this._targetScrollLeft = null;
            }
        });
    }

    private get targetScrollLeft(): number {
        return this._targetScrollLeft !== null ? this._targetScrollLeft : this._track.scrollLeft;
    }

    private updateNavIcons(): void {
        //No overflow
        if (this._track.scrollWidth <= this._track.offsetWidth) {
            this._prevIcon.classList.remove("active");
            this._nextIcon.classList.remove("active");
        } else {
            this._prevIcon.classList.toggle("active", this.targetScrollLeft != 0);
            var maxScroll = this._track.scrollWidth - this._track.offsetWidth;
            this._nextIcon.classList.toggle("active", this.targetScrollLeft < maxScroll);
        }

        this._next.classList.toggle("active", this._nextIcon.classList.contains("active"));
        this._prev.classList.toggle("active", this._prevIcon.classList.contains("active"));
    }

    public set category(value: VOCategory) {
        if (!value || this._category == value) {
            return;
        }
        this._category = value;

        this.updateVisibility();
    }

    private updateVisibility(): void {
        var hasFirst = false;
        for (var item of this._items) {
            var visible = !this._category || item.video.categories.indexOf(this._category) !== -1;
            if (visible && item.video.languages != "all") {
                visible = item.video.languages == Language.current;
            }
            item.visible = visible;
            item.element.classList.toggle("first", item.visible && !hasFirst);
            if (!hasFirst && item.visible) {
                hasFirst = true;
            }
        }
        this._videosVisible = this._items.filter((video) => video.visible);
    }

    public get category(): VOCategory {
        return this._category;
    }

    public get video(): VOVideo {
        return this._video;
    }

    public set video(value: VOVideo) {
        if (this._video == value) {
            return;
        }
        this._video = value;

        var first: VideoSelectorItem = null;
        for (var item of this._items) {
            item.active = item.video == value;
            if (!first && item.active) {
                first = item;
            }
        }

        this.scrollIntoView(value);
    }

    private scrollIntoView(video: VOVideo, center: boolean = true): void {
        var first: VideoSelectorItem = null;
        for (var item of this._items) {
            if (item.video == video) {
                first = item;
                break;
            }
        }

        if (first) {
            if (center) {
                this.scrollTo((first.element.offsetLeft + first.element.offsetWidth * 0.5) - this._track.offsetWidth * 0.5);
            } else {
                var scrollLeft = this.targetScrollLeft;
                var left = first.element.offsetLeft;
                var right = left + first.element.offsetWidth;
                if (left < scrollLeft) {
                    this.scrollTo(left);
                } else if (right > scrollLeft + this._track.offsetWidth) {
                    this.scrollTo(right - this._track.offsetWidth);
                }
            }
        }
    }
}

class VideoSelectorItem {
    public element: HTMLDivElement;

    constructor(public video: VOVideo) {
        this.element = document.createElement("div");
        this.element.className = "video-selector-item";
        this.element.setAttribute("data-videoid", video.id + "");
        this.element.innerHTML = `
        <canvas class="video-selector-item-ratio" width="16" height="9"></canvas>
        <div class="video-selector-item-text"></div>
        <div class="video-selector-item-active">
            <div class="video-selector-item-active-triangle"></div>
        </div>
        `;

        var text = this.element.querySelector(".video-selector-item-text") as HTMLDivElement;
        Language.apply(text, video.title_de, video.title_en);

        if (video.thumbnail_de == video.thumbnail_en) {
            this.element.style.backgroundImage = `url(${video.thumbnail_de})`;
        } else {
            Language.apply(this.element, `background-image: url(${video.thumbnail_de})`, `background-image: url(${video.thumbnail_en})`, "style");
        }
    }

    public get visible(): boolean {
        return !this.element.classList.contains("invisible");
    }

    public set visible(value: boolean) {
        this.element.classList.toggle("invisible", !value);
    }

    public get active(): boolean {
        return this.element.classList.contains("active");
    }

    public set active(value: boolean) {
        this.element.classList.toggle("active", value);
    }
}